import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getStats } from "@/lib/admin";
import { useAdminQuery } from "@/hooks/useAdminQuery";
import { StatCard } from "@/components/ui/stat-card";

const SECTOR_COLORS: Record<string, string> = { energy: "#059669", agriculture: "#d97706" };
const PALETTE = ["#059669", "#d97706", "#2563eb", "#4f46e5", "#7c3aed", "#dc2626"];
const CHART_HEIGHT = 220;

function EmptyChartState({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center" style={{ height: CHART_HEIGHT }}>
      <div className="flex h-24 w-24 items-center justify-center rounded-full border-2 border-slate-200">
        <p className="px-3 text-center text-xs text-slate-400">{message}</p>
      </div>
    </div>
  );
}

export default function AdminStats() {
  const { data: stats, loading, error } = useAdminQuery(() => getStats(), []);

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (loading || !stats) return <p className="text-sm text-muted-foreground">Loading...</p>;

  const { counts, breakdowns, activity } = stats;
  const mergedActivity = mergeActivity(activity.monthlyNewCompanies, activity.monthlyNewUsers);
  const hasActivity = mergedActivity.some((row) => row.companies > 0 || row.users > 0);

  return (
    <div className="space-y-8">
      <h1 className="text-4xl font-heading font-extrabold">Stats</h1>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        <StatCard label="Companies" value={counts.totalCompanies} />
        <StatCard label="Disabled companies" value={counts.disabledCompanies} />
        <StatCard label="Users" value={counts.totalUsers} />
        <StatCard label="Active users" value={counts.activeUsers} />
        <StatCard label="Disabled users" value={counts.disabledUsers} />
        <StatCard label="Assets" value={counts.totalAssets} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <h2 className="text-lg font-bold leading-7">Companies by sector</h2>
          {breakdowns.companiesBySector.length === 0 ? (
            <EmptyChartState message="No companies yet" />
          ) : (
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <PieChart>
                <Pie data={breakdowns.companiesBySector} dataKey="count" nameKey="sector" outerRadius={80} label>
                  {breakdowns.companiesBySector.map((row) => (
                    <Cell key={row.sector} fill={SECTOR_COLORS[row.sector] ?? "#64748b"} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </section>

        <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <h2 className="text-lg font-bold leading-7">Assets by country</h2>
          {breakdowns.assetsByCountry.length === 0 ? (
            <EmptyChartState message="No assets yet" />
          ) : (
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <BarChart data={breakdowns.assetsByCountry}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="country" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count">
                  {breakdowns.assetsByCountry.map((row, i) => (
                    <Cell key={row.country} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </section>

        <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <h2 className="text-lg font-bold leading-7">Assets by type</h2>
          {breakdowns.assetsByAssetType.length === 0 ? (
            <EmptyChartState message="No assets yet" />
          ) : (
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <BarChart
                data={breakdowns.assetsByAssetType.map((row) => ({
                  ...row,
                  // Sector suffix: some type names exist in both energy and agriculture.
                  label: `${row.assetTypeName ?? "Custom"} (${row.sector})`,
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count">
                  {breakdowns.assetsByAssetType.map((row, i) => (
                    <Cell key={`${row.assetTypeName ?? "custom"}|${row.sector}`} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </section>

        <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <h2 className="text-lg font-bold leading-7">New companies & users (12 months)</h2>
          {!hasActivity ? (
            <EmptyChartState message="No signups in the last 12 months" />
          ) : (
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <LineChart data={mergedActivity}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="companies" stroke="#059669" strokeWidth={2} name="Companies" />
                <Line type="monotone" dataKey="users" stroke="#2563eb" strokeWidth={2} name="Users" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </section>
      </div>
    </div>
  );
}

function mergeActivity(
  companies: { month: string; count: number }[],
  users: { month: string; count: number }[]
) {
  const months = new Set([...companies.map((c) => c.month), ...users.map((u) => u.month)]);
  return Array.from(months)
    .sort()
    .map((month) => ({
      month,
      companies: companies.find((c) => c.month === month)?.count ?? 0,
      users: users.find((u) => u.month === month)?.count ?? 0,
    }));
}
